import React, { useState, useEffect } from 'react';
import { getChatDetails } from '../services/api';
import './ChatDetailPage.css';

const ChatDetailPage = ({ chatId, onClose }) => {
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChatDetails = async () => {
    try {
      setLoading(true);
      const data = await getChatDetails(chatId);
      setChatData(data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching chat details:', err);
      setError('Failed to fetch chat details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatDetails();
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const renderStars = (rating) => {
    if (!rating) return 'No rating';
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading && !chatData) {
    return (
      <div className="chat-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading chat details...</p>
        </div>
      </div>
    );
  }

  if (error && !chatData) {
    return (
      <div className="chat-detail-page">
        <div className="error-container">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={onClose}>Back</button>
        </div>
      </div>
    );
  }

  const totalDuration = chatData?.chat_duration || 0;
  const waitDuration = chatData?.wait_duration || 0;
  return (
    <div className="chat-detail-page">
      {/* Header */}
      <div className="chat-header">
        <button className="back-btn-chat" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        
        <div className="chat-header-info">
          <h1>Chat Details</h1>
          <div className="chat-id-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {chatId}
          </div>
          <div className={`chat-status-badge ${chatData?.status}`}>
            {chatData?.status || 'Unknown'}
          </div>
          <div className="chat-type-badge">
            {chatData?.chat_type || 'Web'}
          </div>
        </div>
      </div>

      {/* Chat Overview Cards */}
      <div className="chat-metrics-grid">
        <div className="metric-card-chat">
          <div className="metric-icon-chat messages">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div className="metric-info-chat">
            <div className="metric-value-chat">{chatData?.message_count || 0}</div>
            <div className="metric-label-chat">Total Messages</div>
          </div>
        </div>

        <div className="metric-card-chat">
          <div className="metric-icon-chat responses">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </div>
          <div className="metric-info-chat">
            <div className="metric-value-chat">{chatData?.response_count || 0}</div>
            <div className="metric-label-chat">Agent Responses</div>
          </div>
        </div>

        <div className="metric-card-chat">
          <div className="metric-icon-chat duration">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="metric-info-chat">
            <div className="metric-value-chat">{formatDuration(totalDuration)}</div>
            <div className="metric-label-chat">Chat Duration</div>
          </div>
        </div>

        <div className="metric-card-chat">
          <div className="metric-icon-chat response-time">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div className="metric-info-chat">
            <div className="metric-value-chat">{chatData?.average_response_time || 0}s</div>
            <div className="metric-label-chat">Avg Response Time</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="chat-chart-section">
        {/* Time Breakdown */}
        <div className="chart-card-chat">
          <h3 className="chart-heading-chat">Time Breakdown</h3>
          <div className="chat-time-breakdown">
            <div className="time-row-chat">
              <div className="time-label-chat">
                <span className="time-dot-chat wait"></span>
                Wait Time
              </div>
              <div className="time-bar-container-chat">
                <div 
                  className="time-bar-chat wait"
                  style={{ width: `${(waitDuration / (waitDuration + totalDuration)) * 100}%` }}
                >
                  <span className="time-text-chat">{formatDuration(waitDuration)}</span>
                </div>
              </div>
              <div className="time-value-chat">{Math.round((waitDuration / (waitDuration + totalDuration)) * 100)}%</div>
            </div>

            <div className="time-row-chat">
              <div className="time-label-chat">
                <span className="time-dot-chat chat"></span>
                Chat Time
              </div>
              <div className="time-bar-container-chat">
                <div 
                  className="time-bar-chat chat"
                  style={{ width: `${(totalDuration / (waitDuration + totalDuration)) * 100}%` }}
                >
                  <span className="time-text-chat">{formatDuration(totalDuration)}</span>
                </div>
              </div>
              <div className="time-value-chat">{Math.round((totalDuration / (waitDuration + totalDuration)) * 100)}%</div>
            </div>
          </div>
        </div>

        {/* Response Metrics */}
        <div className="chart-card-chat">
          <h3 className="chart-heading-chat">Response Metrics</h3>
          <div className="response-metrics-chart">
            <div className="response-metric-item">
              <div className="response-metric-label">Average</div>
              <div className="response-metric-value">{chatData?.average_response_time || 0}s</div>
              <div className="response-metric-bar">
                <div className="response-bar-fill avg" style={{ width: '60%' }}></div>
              </div>
            </div>

            <div className="response-metric-item">
              <div className="response-metric-label">Longest</div>
              <div className="response-metric-value">{chatData?.longest_response_time || 0}s</div>
              <div className="response-metric-bar">
                <div className="response-bar-fill max" style={{ width: '100%' }}></div>
              </div>
            </div>

            <div className="response-metric-item">
              <div className="response-metric-label">Total Responses</div>
              <div className="response-metric-value">{chatData?.response_count || 0}</div>
              <div className="response-metric-bar">
                <div className="response-bar-fill count" style={{ 
                  width: `${((chatData?.response_count || 0) / (chatData?.message_count || 1)) * 100}%` 
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Timeline */}
      <div className="chat-timeline-section">
        <h3 className="section-heading-chat">Chat Timeline</h3>
        <div className="chat-timeline">
          <div className="timeline-item-chat">
            <div className="timeline-icon-chat created">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="timeline-content-chat">
              <div className="timeline-title-chat">Chat Created</div>
              <div className="timeline-time-chat">{formatDateTime(chatData?.created_at)}</div>
            </div>
          </div>

          <div className="timeline-item-chat">
            <div className="timeline-icon-chat queue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="timeline-content-chat">
              <div className="timeline-title-chat">In Queue</div>
              <div className="timeline-time-chat">Wait: {formatDuration(waitDuration)}</div>
            </div>
          </div>

          <div className="timeline-item-chat">
            <div className="timeline-icon-chat assigned">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="timeline-content-chat">
              <div className="timeline-title-chat">Assigned to {chatData?.agent_info?.name || 'Agent'}</div>
              <div className="timeline-time-chat">{formatDateTime(chatData?.assigned_at)}</div>
            </div>
          </div>

          <div className="timeline-item-chat">
            <div className="timeline-icon-chat ended">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="timeline-content-chat">
              <div className="timeline-title-chat">Chat Ended</div>
              <div className="timeline-time-chat">{formatDateTime(chatData?.ends_at)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Satisfaction */}
      {chatData?.rating && (
        <div className="chat-rating-section">
          <h3 className="section-heading-chat">Customer Satisfaction</h3>
          <div className="rating-display">
            <div className="stars-large">{renderStars(chatData.rating)}</div>
            <div className="rating-score">{chatData.rating}/5</div>
            {chatData.has_feedback && (
              <div className="feedback-indicator">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Customer provided feedback
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Information */}
      <div className="chat-info-section">
        <h3 className="section-heading-chat">Chat Information</h3>
        <div className="info-grid-chat">
          <div className="info-card-chat">
            <div className="info-label-chat">Chat ID</div>
            <div className="info-value-chat">{chatId}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Chat Type</div>
            <div className="info-value-chat" style={{ textTransform: 'capitalize' }}>
              {chatData?.chat_type || 'N/A'}
            </div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Status</div>
            <div className="info-value-chat" style={{ textTransform: 'capitalize' }}>
              {chatData?.status || 'N/A'}
            </div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Language</div>
            <div className="info-value-chat">{chatData?.lang?.toUpperCase() || 'N/A'}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Customer</div>
            <div className="info-value-chat">{chatData?.end_user?.identifier || 'N/A'}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Agent</div>
            <div className="info-value-chat">{chatData?.agent_info?.name || 'N/A'}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Menu Path</div>
            <div className="info-value-chat">{chatData?.menu_path || 'N/A'}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Total Duration</div>
            <div className="info-value-chat">{formatDuration(totalDuration)}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Wait Duration</div>
            <div className="info-value-chat">{formatDuration(waitDuration)}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Messages</div>
            <div className="info-value-chat">{chatData?.message_count || 0}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Agent Responses</div>
            <div className="info-value-chat">{chatData?.response_count || 0}</div>
          </div>
          <div className="info-card-chat">
            <div className="info-label-chat">Avg Response</div>
            <div className="info-value-chat">{chatData?.average_response_time || 0}s</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatDetailPage;



